import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../axios";
import toast from "../hooks/toast";
import { buildApiV3Url } from "../shared/Utils";
import type { RootState } from "./types";

// -----------------------------------------------------------------------------
// CERTCAP-72798 — Exemption status (ecms-sync-report).
// GET/POST/DELETE /api/v3/companies/{companyId}/ecms-sync-report
// -----------------------------------------------------------------------------

export interface EcmsSyncArtifactLink {
    fileName: string;
    downloadUrl: string;
}

export interface EcmsSyncReportView {
    runId: number;
    status: string;
    source: string | null;
    avataxCompanyId: number;
    matchPct: number | null;
    desiredCount: number | null;
    actualCount: number | null;
    matchedCount: number | null;
    missingCount: number | null;
    orphanCount: number | null;
    errorMessage: string | null;
    reportPayload: Record<string, unknown> | null;
    artifacts: EcmsSyncArtifactLink[];
    created: string | null;
    modified: string | null;
}

export interface EcmsSyncReportRunSummary {
    runId: number;
    status: string;
    source: string | null;
    matchPct: number | null;
    desiredCount: number | null;
    actualCount: number | null;
    matchedCount: number | null;
    missingCount: number | null;
    orphanCount: number | null;
    errorMessage: string | null;
    hasArtifacts: boolean;
    created: string | null;
    modified: string | null;
}

export type EcmsSyncBucketKey = "ready" | "mayNotExempt" | "mayStillExempt";

export interface EcmsSyncReportDetailRow {
    certificateId: string | null;
    customerCode: string | null;
    expiryDate: string | null;
    statusNote: string | null;
    whatToDo: string | null;
    hasKnownSyncError: boolean | null;
    syncErrorMessage: string | null;
}

export interface EcmsSyncReportDetailBucket {
    key: string;
    label: string;
    description: string;
    artifactFileName: string | null;
    totalCount: number;
    truncated: boolean;
    rows: EcmsSyncReportDetailRow[];
}

export interface EcmsSyncReportDetails {
    runId: number;
    rowLimit: number;
    readyToApply: EcmsSyncReportDetailBucket;
    mayNotExempt: EcmsSyncReportDetailBucket;
    mayStillExempt: EcmsSyncReportDetailBucket;
}

export interface EcmsSyncReportState {
    isLoading: boolean;
    isMutating: boolean;
    isRunsLoading: boolean;
    isDetailsLoading: boolean;
    report: EcmsSyncReportView | null;
    runs: EcmsSyncReportRunSummary[];
    details: EcmsSyncReportDetails | null;
    selectedBucket: EcmsSyncBucketKey;
    hasLoaded: boolean;
}

const initialState: EcmsSyncReportState = {
    isLoading: false,
    isMutating: false,
    isRunsLoading: false,
    isDetailsLoading: false,
    report: null,
    runs: [],
    details: null,
    selectedBucket: "mayNotExempt",
    hasLoaded: false,
};

export const selectEcmsSyncReportIsLoading = (state: RootState): boolean =>
    state.ecmsSyncReport.isLoading;
export const selectEcmsSyncReportIsMutating = (state: RootState): boolean =>
    state.ecmsSyncReport.isMutating;
export const selectEcmsSyncReportIsRunsLoading = (state: RootState): boolean =>
    state.ecmsSyncReport.isRunsLoading;
export const selectEcmsSyncReportIsDetailsLoading = (state: RootState): boolean =>
    state.ecmsSyncReport.isDetailsLoading;
export const selectEcmsSyncReport = (state: RootState): EcmsSyncReportView | null =>
    state.ecmsSyncReport.report;
export const selectEcmsSyncReportRuns = (state: RootState): EcmsSyncReportRunSummary[] =>
    state.ecmsSyncReport.runs;
export const selectEcmsSyncReportDetails = (state: RootState): EcmsSyncReportDetails | null =>
    state.ecmsSyncReport.details;
export const selectEcmsSyncSelectedBucket = (state: RootState): EcmsSyncBucketKey =>
    state.ecmsSyncReport.selectedBucket;
export const selectEcmsSyncReportHasLoaded = (state: RootState): boolean =>
    state.ecmsSyncReport.hasLoaded;

const reportBaseUrl = (state: RootState): string | null => {
    const companyId = state.session.activeCompany?.id;
    if (companyId == null || companyId === "") {
        return null;
    }
    return buildApiV3Url(`companies/${companyId}/ecms-sync-report`);
};

export const fetchEcmsSyncReport = createAsyncThunk<
    EcmsSyncReportView | null,
    void,
    { state: RootState; rejectValue: string }
>("ecmsSyncReport/fetch", async (_, { getState, rejectWithValue }) => {
    const baseUrl = reportBaseUrl(getState());
    if (!baseUrl) {
        return rejectWithValue("No active company");
    }
    try {
        const response = await axios.get<EcmsSyncReportView>(baseUrl, {
            withCredentials: true,
            validateStatus: status => status === 200 || status === 404,
        });
        if (response.status === 404 || !response.data?.runId) {
            return null;
        }
        return response.data;
    } catch {
        const [showToast] = toast();
        showToast("error", "Failed to load Exemption status report.");
        return rejectWithValue("Failed to load report");
    }
});

export const fetchEcmsSyncReportRuns = createAsyncThunk<
    EcmsSyncReportRunSummary[],
    void,
    { state: RootState; rejectValue: string }
>("ecmsSyncReport/fetchRuns", async (_, { getState, rejectWithValue }) => {
    const baseUrl = reportBaseUrl(getState());
    if (!baseUrl) {
        return rejectWithValue("No active company");
    }
    try {
        const { data } = await axios.get<EcmsSyncReportRunSummary[]>(`${baseUrl}/runs`, {
            withCredentials: true,
            params: { limit: 50 },
        });
        return Array.isArray(data) ? data : [];
    } catch {
        const [showToast] = toast();
        showToast("error", "Failed to load previous report runs.");
        return rejectWithValue("Failed to load runs");
    }
});

export const fetchEcmsSyncReportById = createAsyncThunk<
    EcmsSyncReportView | null,
    number,
    { state: RootState; rejectValue: string }
>("ecmsSyncReport/fetchById", async (runId, { getState, rejectWithValue }) => {
    const baseUrl = reportBaseUrl(getState());
    if (!baseUrl) {
        return rejectWithValue("No active company");
    }
    try {
        const response = await axios.get<EcmsSyncReportView>(`${baseUrl}/${runId}`, {
            withCredentials: true,
            validateStatus: status => status === 200 || status === 404,
        });
        if (response.status === 404 || !response.data?.runId) {
            return null;
        }
        return response.data;
    } catch {
        const [showToast] = toast();
        showToast("error", "Failed to load selected report run.");
        return rejectWithValue("Failed to load run");
    }
});

export const fetchEcmsSyncReportDetails = createAsyncThunk<
    EcmsSyncReportDetails | null,
    number,
    { state: RootState; rejectValue: string }
>("ecmsSyncReport/fetchDetails", async (runId, { getState, rejectWithValue }) => {
    const baseUrl = reportBaseUrl(getState());
    if (!baseUrl) {
        return rejectWithValue("No active company");
    }
    try {
        const response = await axios.get<EcmsSyncReportDetails>(`${baseUrl}/${runId}/details`, {
            withCredentials: true,
            validateStatus: status => status === 200 || status === 404,
        });
        if (response.status === 404 || !response.data?.runId) {
            return null;
        }
        return response.data;
    } catch {
        const [showToast] = toast();
        showToast("error", "Failed to load certificate details for this report.");
        return rejectWithValue("Failed to load details");
    }
});

export const queueEcmsSyncReport = createAsyncThunk<
    { runId?: number; message?: string },
    void,
    { state: RootState; rejectValue: string }
>("ecmsSyncReport/queue", async (_, { getState, rejectWithValue }) => {
    const baseUrl = reportBaseUrl(getState());
    if (!baseUrl) {
        return rejectWithValue("No active company");
    }
    try {
        const { data } = await axios.post<{ success: boolean; message?: string; runId?: number }>(
            baseUrl,
            null,
            { withCredentials: true }
        );
        const [showToast] = toast();
        if (!data?.success) {
            showToast("error", data?.message || "Failed to queue report.");
            return rejectWithValue(data?.message || "Failed to queue");
        }
        showToast("success", data.message || "Report queued.");
        return { runId: data.runId, message: data.message };
    } catch {
        const [showToast] = toast();
        showToast("error", "Failed to queue Exemption status report.");
        return rejectWithValue("Failed to queue");
    }
});

export const deleteEcmsSyncReport = createAsyncThunk<
    void,
    number,
    { state: RootState; rejectValue: string }
>("ecmsSyncReport/delete", async (runId, { getState, rejectWithValue }) => {
    const baseUrl = reportBaseUrl(getState());
    if (!baseUrl) {
        return rejectWithValue("No active company");
    }
    try {
        const { data } = await axios.delete<{ success: boolean; message?: string }>(
            `${baseUrl}/${runId}`,
            { withCredentials: true }
        );
        const [showToast] = toast();
        if (!data?.success) {
            showToast("error", data?.message || "Failed to delete report.");
            return rejectWithValue(data?.message || "Failed to delete");
        }
        showToast("success", data.message || "Report deleted.");
        return undefined;
    } catch {
        const [showToast] = toast();
        showToast("error", "Failed to delete Exemption status report.");
        return rejectWithValue("Failed to delete");
    }
});

export const downloadEcmsSyncArtifact = createAsyncThunk<
    void,
    { runId: number; fileName: string },
    { state: RootState; rejectValue: string }
>("ecmsSyncReport/downloadArtifact", async ({ runId, fileName }, { getState, rejectWithValue }) => {
    const baseUrl = reportBaseUrl(getState());
    if (!baseUrl) {
        return rejectWithValue("No active company");
    }
    try {
        const response = await axios.get(`${baseUrl}/${runId}/artifacts`, {
            withCredentials: true,
            responseType: "blob",
            params: { fileName },
        });
        const blob = new Blob([response.data], { type: "text/csv" });
        const objectUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(objectUrl);
        return undefined;
    } catch {
        const [showToast] = toast();
        showToast("error", `Failed to download ${fileName}.`);
        return rejectWithValue("Failed to download");
    }
});

const ecmsSyncReportSlice = createSlice({
    name: "ecmsSyncReport",
    initialState,
    reducers: {
        clearEcmsSyncReport: state => {
            state.report = null;
            state.runs = [];
            state.details = null;
            state.selectedBucket = "mayNotExempt";
            state.hasLoaded = false;
        },
        setEcmsSyncSelectedBucket: (state, action: { payload: EcmsSyncBucketKey }) => {
            state.selectedBucket = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchEcmsSyncReport.pending, state => {
                state.isLoading = true;
            })
            .addCase(fetchEcmsSyncReport.fulfilled, (state, action) => {
                state.isLoading = false;
                state.hasLoaded = true;
                state.report = action.payload;
                state.details = null;
                state.selectedBucket = "mayNotExempt";
            })
            .addCase(fetchEcmsSyncReport.rejected, state => {
                state.isLoading = false;
                state.hasLoaded = true;
            })
            .addCase(fetchEcmsSyncReportById.pending, state => {
                state.isLoading = true;
            })
            .addCase(fetchEcmsSyncReportById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.report = action.payload;
                state.details = null;
                state.selectedBucket = "mayNotExempt";
            })
            .addCase(fetchEcmsSyncReportById.rejected, state => {
                state.isLoading = false;
            })
            .addCase(fetchEcmsSyncReportRuns.pending, state => {
                state.isRunsLoading = true;
            })
            .addCase(fetchEcmsSyncReportRuns.fulfilled, (state, action) => {
                state.isRunsLoading = false;
                state.runs = action.payload;
            })
            .addCase(fetchEcmsSyncReportRuns.rejected, state => {
                state.isRunsLoading = false;
            })
            .addCase(fetchEcmsSyncReportDetails.pending, state => {
                state.isDetailsLoading = true;
            })
            .addCase(fetchEcmsSyncReportDetails.fulfilled, (state, action) => {
                state.isDetailsLoading = false;
                state.details = action.payload;
            })
            .addCase(fetchEcmsSyncReportDetails.rejected, state => {
                state.isDetailsLoading = false;
                state.details = null;
            })
            .addCase(queueEcmsSyncReport.pending, state => {
                state.isMutating = true;
            })
            .addCase(queueEcmsSyncReport.fulfilled, state => {
                state.isMutating = false;
            })
            .addCase(queueEcmsSyncReport.rejected, state => {
                state.isMutating = false;
            })
            .addCase(deleteEcmsSyncReport.pending, state => {
                state.isMutating = true;
            })
            .addCase(deleteEcmsSyncReport.fulfilled, state => {
                state.isMutating = false;
                state.report = null;
                state.details = null;
            })
            .addCase(deleteEcmsSyncReport.rejected, state => {
                state.isMutating = false;
            });
    },
});

export const { clearEcmsSyncReport, setEcmsSyncSelectedBucket } = ecmsSyncReportSlice.actions;
export default ecmsSyncReportSlice.reducer;
